import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { UsersService } from '../../services/users.service';
import { PurchasingService } from '../../services/purchasing.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import type { VendorRow } from './vendors.model';

interface UnlinkedBrand {
	manufacturer_id: number;
	brand_name: string;
}

interface SupplierOption {
	supplierid: string;
	suppname: string;
}

export interface BrandOnboardDialogData {
	/** true if the caller has no data to preload (dialog fetches its own). */
	standalone?: boolean;
}

/**
 * Closes the gap between Add Vendor, Matrix Brands, and the Vendors (portal login) screen:
 * one dialog that creates-or-links a brand AND its vendor together, then hands straight off
 * to the existing "Create login" dialog. See onboardbrand() in server users.php controller.
 */
@Component({
	selector: 'app-brand-onboard-dialog',
	templateUrl: './brand-onboard-dialog.component.html',
	styleUrls: ['./brand-onboard-dialog.component.scss'],
})
export class BrandOnboardDialogComponent implements OnInit {
	saving = false;
	loading = true;

	unlinkedBrands: UnlinkedBrand[] = [];
	suppliers: SupplierOption[] = [];
	supplierFilter = new FormControl('');

	form = new FormGroup({
		brandMode: new FormControl<'new' | 'existing'>('new'),
		brandName: new FormControl('', [Validators.required]),
		manufacturerId: new FormControl<number | null>(null),

		vendorMode: new FormControl<'new' | 'existing'>('new'),
		vendorName: new FormControl(''),
		vendorNameTouched: new FormControl(false),
		supplierId: new FormControl(''),

		vendorCurrency: new FormControl('USD'),
		vendorTerms: new FormControl('N30'),
		vendorAddress1: new FormControl(''),
		vendorTaxref: new FormControl(''),
	});

	constructor(
		private dialogRef: MatDialogRef<BrandOnboardDialogComponent, { created: boolean; row?: VendorRow }>,
		@Inject(MAT_DIALOG_DATA) public data: BrandOnboardDialogData,
		private usersService: UsersService,
		private purchasingService: PurchasingService,
		private globalSearchService: GlobalSearchService
	) {}

	ngOnInit(): void {
		forkJoin({
			unlinked: this.usersService.getUnlinkedBrands(),
			suppliers: this.purchasingService.getSuppliers(),
		}).subscribe({
			next: ({ unlinked, suppliers }) => {
				this.unlinkedBrands = (Array.isArray(unlinked) ? unlinked : [])
					.map((b: any) => ({ manufacturer_id: Number(b.manufacturer_id), brand_name: String(b.brand_name || '') }))
					.sort((a, b) => a.brand_name.localeCompare(b.brand_name));
				this.suppliers = (Array.isArray(suppliers) ? suppliers : [])
					.map((s: any) => ({ supplierid: String(s.supplierid || ''), suppname: String(s.suppname || '') }))
					.filter((s) => s.supplierid !== '')
					.sort((a, b) => a.suppname.localeCompare(b.suppname));
				this.loading = false;
			},
			error: () => {
				// Non-fatal — "new brand" + "new vendor" (the common path) needs neither list.
				this.loading = false;
			},
		});

		// Keep vendor name in sync with brand name until the user edits it themselves.
		this.form.get('brandName')?.valueChanges.subscribe((v) => {
			if (!this.form.get('vendorNameTouched')?.value) {
				this.form.get('vendorName')?.setValue(v || '', { emitEvent: false });
			}
		});
	}

	get filteredSuppliers(): SupplierOption[] {
		const term = (this.supplierFilter.value || '').trim().toLowerCase();
		if (!term) {
			return this.suppliers;
		}
		return this.suppliers.filter((s) => `${s.suppname} ${s.supplierid}`.toLowerCase().includes(term));
	}

	onVendorNameEdited(): void {
		this.form.get('vendorNameTouched')?.setValue(true);
	}

	setBrandMode(mode: 'new' | 'existing'): void {
		this.form.get('brandMode')?.setValue(mode);
		if (mode === 'existing' && !this.form.get('manufacturerId')?.value && this.unlinkedBrands.length) {
			this.form.get('manufacturerId')?.setValue(this.unlinkedBrands[0].manufacturer_id);
		}
	}

	setVendorMode(mode: 'new' | 'existing'): void {
		this.form.get('vendorMode')?.setValue(mode);
	}

	selectSupplier(s: SupplierOption): void {
		this.form.get('supplierId')?.setValue(s.supplierid);
		this.supplierFilter.setValue(`${s.suppname} (${s.supplierid})`);
	}

	get canSubmit(): boolean {
		const v = this.form.getRawValue();
		if (v.brandMode === 'new' && !(v.brandName || '').trim()) { return false; }
		if (v.brandMode === 'existing' && !v.manufacturerId) { return false; }
		if (v.vendorMode === 'existing' && !(v.supplierId || '').trim()) { return false; }
		return true;
	}

	cancel(): void {
		this.dialogRef.close({ created: false });
	}

	submit(): void {
		if (!this.canSubmit || this.saving) {
			return;
		}
		const v = this.form.getRawValue();
		const payload: Record<string, unknown> = {
			mode: v.vendorMode,
		};
		if (v.brandMode === 'existing') {
			payload['manufacturer_id'] = v.manufacturerId;
		} else {
			payload['brand_name'] = (v.brandName || '').trim();
		}
		if (v.vendorMode === 'existing') {
			payload['supplier_id'] = v.supplierId;
		} else {
			payload['vendor_name'] = (v.vendorName || '').trim();
			payload['vendor_currency'] = v.vendorCurrency;
			payload['vendor_terms'] = v.vendorTerms;
			payload['vendor_address1'] = v.vendorAddress1;
			payload['vendor_taxref'] = v.vendorTaxref;
		}

		this.saving = true;
		this.usersService.onboardBrand(payload).subscribe({
			next: (res: any) => {
				this.saving = false;
				if (res?.success && res.row) {
					this.dialogRef.close({ created: true, row: res.row });
				} else {
					this.globalSearchService.showNotification(res?.message || 'Could not add brand', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.saving = false;
				const msg = err?.error?.message || err?.message || 'Could not add brand';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			},
		});
	}
}
